import type { JobDetails } from '@seek/ca-graphql-schema/types';
import { JobDetailsView } from '@seek/job-details-view';
import { metrics } from '@seek/metrics-js';
import { ActivityType, PageName, getJobDetailsMetadata } from '@seek/sol-js';
import { Box } from 'braid-design-system';
import { useEffect, useMemo } from 'react';

import { useSolHashByJobId } from 'src/context/solContext/solContext.tsx';
import type { GetJobDetailsQuery } from 'src/graphql/graphql';
import useFetchJobDetails from 'src/hooks/jobDetails/useFetchJobDetails';
import useJobDetailsProps from 'src/hooks/jobDetails/useJobDetailsProps';
import useHubbleTrackActivity from 'src/hooks/useHubbleTrackActivity.ts';
import type { RecommendedGlobalJob } from 'src/queries/types';

import { FeatureFlagsName, isFeatureFlag } from '../../modules/featureFlag.ts';
import { useAppConfig } from '../App/useAppConfig.ts';
import Error from '../Error/Error';
import Loading from '../Loading/Loading';

import {
  isJobProfileMissingSalaryPreference,
  isJobProfileSalaryMatch,
  mapChaliceJobtoDataLayer,
} from './mapChaliceJobToDataLayer';
import useJobDetailsViewed from './useJobDetailsViewed';

const JobDetail = ({
  jobDetails,
  selectedJob,
  selectedJobListingPosition,
}: {
  jobDetails: NonNullable<NonNullable<GetJobDetailsQuery>['jobDetails']>;
  selectedJob: RecommendedGlobalJob;
  selectedJobListingPosition?: number;
}) => {
  const personalisedSalaryMatch = jobDetails.personalised?.salaryMatch;
  const isSalaryMatch = isJobProfileSalaryMatch(personalisedSalaryMatch);
  const isAddSalaryPrefNudgeShown = isJobProfileMissingSalaryPreference(
    personalisedSalaryMatch,
  );
  const solMetadataHash = useSolHashByJobId(selectedJob.job.id);

  const solMetadata = useMemo(
    () =>
      getJobDetailsMetadata({
        jobId: selectedJob.job.id,
        token: selectedJob.solMetadata.token,
        key: solMetadataHash,
      }),
    [selectedJob.job.id, selectedJob.solMetadata.token, solMetadataHash],
  );

  const jobDetailProps = useJobDetailsProps({
    jobDetails: jobDetails as JobDetails,
    isSaved: Boolean(selectedJob.isSaved),
    isSalaryMatch,
    isAddSalaryPrefNudgeShown,
    solMetadata,
  });

  useEffect(() => {
    metrics.count('jodDetailViewed');
  }, []);

  useHubbleTrackActivity({
    trackType: ActivityType.REQUEST_JOB_DETAILS,
    solMetadata,
    page: PageName.RECOMMENDATIONS,
    topApplicantBadgeShown: Boolean(jobDetails.personalised?.topApplicantBadge),
    linkout: Boolean(jobDetails.job.isLinkOut),
    isSalaryMatch,
    isAddSalaryPrefNudgeShown,
  });

  const memoizedMapChaliceJobtoDataLayer = useMemo(
    () =>
      mapChaliceJobtoDataLayer({
        jobDetails,
        selectedJobListingPosition,
      }),
    [jobDetails, selectedJobListingPosition],
  );

  useJobDetailsViewed(memoizedMapChaliceJobtoDataLayer);

  const {
    REDUCED_DUPLICATED_APPLY,
    STICK_APPLY_BUTTON_ON_DESKTOP: stickApplyButtonOnDesktop,
  } = useAppConfig().zoneFeatures;

  const isReduceDuplicatedApplyEnabled =
    isFeatureFlag(FeatureFlagsName.REDUCED_DUPLICATED_APPLY) ||
    REDUCED_DUPLICATED_APPLY;

  return (
    <Box
      data-automation="jobView"
      paddingRight="xsmall"
      paddingTop={
        jobDetailProps.job.branding?.assets.logo?.url ||
        jobDetailProps.job.branding?.assets.cover?.url
          ? 'none'
          : 'xxsmall'
      }
    >
      <JobDetailsView
        {...jobDetailProps}
        featureFlags={{
          enableReducedDuplicatedApply: isReduceDuplicatedApplyEnabled,
          stickApplyButtonOnDesktop,
          hideApplyButtonOnPrivateAdvertiser: true,
        }}
      />
    </Box>
  );
};

export default ({
  selectedJob,
  selectedJobListingPosition,
}: {
  selectedJob: RecommendedGlobalJob;
  selectedJobListingPosition?: number;
}) => {
  const { error, isLoading, jobDetails } = useFetchJobDetails(
    selectedJob?.job?.id,
  );

  if (error) {
    return <Error />;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (!selectedJob || !jobDetails) {
    return <></>;
  }

  return (
    <JobDetail
      jobDetails={jobDetails}
      selectedJob={selectedJob}
      selectedJobListingPosition={selectedJobListingPosition}
    />
  );
};
